<template>
  <video
    ref="videoPlayer"
    tabindex="0"
    class="video-js vjs-default-skin vjs-big-play-centered"
    :class="{ 'vjs-fluid': fluid }"
    @keydown.up="setVolume"
    @keydown.down="setVolume"
    @keydown.j="player.currentTime(player.currentTime() - 10)"
    @keydown.l="player.currentTime(player.currentTime() + 10)"
    @keydown.c="toggleCaptions"
    @keydown.v="toggleDV"
  />
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { throttle } from 'lodash';
import config from '#/config';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import 'jb-videojs-hls-quality-selector';

import * as fr from 'video.js/dist/lang/fr.json';
import store from '#/stores';
import { GET_SIGNED_URL } from '@/libvue/apps/catalog/screeners/actions';

import { useI18n } from 'vue-i18n';
const { locale, t } = useI18n();

// Define props
const props = defineProps({
  options: {
    type: Object,
    default: () => ({})
  },
  fluid: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(['onPlayerReady']);

// Define refs
const videoPlayer = ref(null);
let player = null;
let nbError = 0;

const defaultVideoOptions = {
  controls: true,
  preload: 'none',
  // audioPosterMode: true,
  controlBar: {
    skipButtons: {
      forward: 10,
      backward: 10
    },
    currentTimeDisplay: true
  },
  responsive: true,
  playbackRates: [0.5, 0, 75, 1, 1.5, 2],
  fluid: props.fluid,
  html5: {
    vhs: {
      overrideNative: true
    },
    nativeAudioTracks: false,
    nativeVideoTracks: false
  }
};

const fetchNewUrl = () => {
  return store
    .dispatch(GET_SIGNED_URL, props.options.screener.uid)
    .then((data) => {
      return data;
    })
    .catch((error) => {
      throw error;
    });
};

const updateVideoSource = async (time = 0) => {
  const newUrl = await fetchNewUrl();
  player.currentTime(time);
  player.src({ type: 'application/x-mpegURL', src: newUrl.data.signed_url });
  player.play();
};

// Initialize video.js player on mounted
onMounted(() => {
  const videoOptions = {
    ...defaultVideoOptions,
    ...props.options
  };
  videojs.addLanguage('fr', {
    ...fr,
    ...fr.default,
    Quality: 'Qualité',
    Francais: 'Français',
    'Audio Track' : 'Vidéodescription (V)',
    'Captions': 'Sous-titres (C)',
    'Subtitles': 'Sous-titres (C)',
    'Fullscreen': 'Plein écran (F)',
    'Skip backward {1} seconds': 'Reculer de {1} secondes (J)',
    'Skip forward {1} seconds': 'Avancer de {1} secondes (L)',
    'Mute': 'Mettre en sourdine (M)',
    'Unmute': 'Activer le son (M)',
    'Pause': 'Pause (K)',
    'Play': 'Lecture (K)'
  });
  videojs.addLanguage('en', {
    'Audio Track': 'Described Video (V)',
    'Captions': 'Closed Captions (C)',
    'Subtitles': 'Closed Captions (C)',
    'Fullscreen': 'Fullscreen (F)',
    'Skip backward {1} seconds': 'Skip backward {1} seconds (J)',
    'Skip forward {1} seconds': 'Skip forward {1} seconds (L)',
    'Mute': 'Mute (M)',
    'Unmute': 'Unmute (M)',
    'Pause': 'Pause (K)',
    'Play': 'Play (K)'
  });
  const sessionId = ref('');

  player = videojs(videoPlayer.value, videoOptions, () => {
    emit('onPlayerReady', player);
    player.hlsQualitySelector({
      displayCurrentQuality: true
    });

    const updatePlayhead = throttle(function (e) {
      if (e) {
        if (typeof window.analytics_media_updatePlayhead === 'function') {
          window.analytics_media_updatePlayhead(
            sessionId.value,
            Math.floor(player.currentTime())
          );
        }
      }
    }, 1000);
    // Events
    // error
    player.on('error', () => {
      const mediaError = player.error();
      const currentTime = player.currentTime();

      if ((mediaError.code === 4 || mediaError.code === 2) && nbError < 1) {
        nbError++;
        updateVideoSource(currentTime);
      }
    });
    // mediaMetadataLoaded (loadedmetadata)
    player.on('loadedmetadata', async () => {
      const screener = props.options.screener;
      const playhead = Math.floor(player.currentTime());
      const playerName = config.decodedHost;
      const audioTracks = player.audioTracks();
      const textTracks = player.textTracks();

      //AUDIO TRACKS LABELS AND CHANGE HANDLER
      if (audioTracks) {
        const audioButton = player.controlBar.getChild('audioTrackButton');

        // Labels des pistes audio
        for (let i = 0; i < audioTracks.length; i++) {
          const track = audioTracks[i];
          if (track.kind === 'main') {
            if (locale.value === 'en') {
              track.label = 'Described Video Off';
            } else {
              track.label = 'Vidéodescription désactivée';
            }
          } else if (
            track.kind === 'descriptions' ||
            track.kind === 'main-desc'
          ) {
            if (locale.value === 'en') {
              track.label = 'Described Video On';
            } else {
              track.label = 'Vidéodescription activée';
            }
          }
        }

        // Fonction pour vérifier et mettre à jour l'état du bouton audio
        const updateAudioButtonState = () => {
          let isDVActive = false;

          for (let i = 0; i < audioTracks.length; i++) {
            const track = audioTracks[i];
            if ((track.kind === 'descriptions' || track.kind === 'main-desc') && track.enabled) {
              isDVActive = true;
              break;
            }
          }

          if (audioButton) {
            if (isDVActive) {
              audioButton.addClass('vjs-audio-dv-active');
            } else {
              audioButton.removeClass('vjs-audio-dv-active');
            }
          }
        };

        // Vérifier l'état initial immédiatement
        updateAudioButtonState();

        // Ajouter le listener pour les changements
        audioTracks.on('change', () => {
          updateAudioButtonState();
        });
      }
      //TEXT TRACKS - CHANGE HANDLER
      if (textTracks) {
        const subsButton = player.controlBar.getChild('SubsCapsButton');

        // Fonction pour vérifier et mettre à jour l'état du bouton
        const updateCaptionsButtonState = () => {
          let isActive = false;

          for (let i = 0; i < textTracks.length; i++) {
            const track = textTracks[i];
            if (track.language !== '' && track.mode === 'showing') {
              isActive = true;
              break;
            }
          }

          if (subsButton) {
            if (isActive) {
              subsButton.addClass('vjs-captions-active');
            } else {
              subsButton.removeClass('vjs-captions-active');
            }
          }
        };

        // Vérifier l'état initial immédiatement
        updateCaptionsButtonState();
        // Ajouter les listeners sur chaque piste avec addEventListener
        for (let i = 0; i < textTracks.length; i++) {
          const track = textTracks[i];
          // Utiliser addEventListener au lieu de .on() pour la compatibilité Safari
          track.addEventListener('modechange', updateCaptionsButtonState);
        }

        // Écouter aussi l'événement global texttrackchange
        player.on('texttrackchange', updateCaptionsButtonState);

        // Pour Safari : ajouter un polling de vérification sinon icône restera rouge quand captions désactivées
        if (videojs.browser.IS_SAFARI) {
          let lastCaptionState = false;

          const safariCaptionPoller = setInterval(() => {
            let currentState = false;

            for (let i = 0; i < textTracks.length; i++) {
              if (textTracks[i].language !== '' && textTracks[i].mode === 'showing') {
                currentState = true;
                break;
              }
            }

            if (currentState !== lastCaptionState) {
              lastCaptionState = currentState;
              updateCaptionsButtonState();
            }
          }, 250);

          player.on('dispose', () => {
            clearInterval(safariCaptionPoller);
          });
        }


      }

      const payload = {
        event: 'mediaMetadataLoaded',
        content: {
          id: screener.uid,
          title: screener.title,
          length: screener.duration,
          type: screener.type
        }
      };
      window.dataLayerQueue.push(payload);

      // get sessionId
      if (typeof window.analytics_media_init === 'function') {
        sessionId.value = await window.analytics_media_init(
          screener.uid,
          playhead,
          playerName
        );
      }


    });
    // timeupdate
    player.on('timeupdate', (e) => {
      updatePlayhead(e);
    });
    // contentPlay (playing)
    player.on('playing', async () => {
      if (!sessionId.value) return;
      // send all events for metrie purposes
      const events = ['mediaStart', 'contentStart', 'contentPlay'];
      const playhead = Math.floor(player.currentTime());

      const dataLayerEvents = events.map((event) => ({
        event,
        sessionId: sessionId.value,
        playhead
      }));

      window.dataLayerQueue.push(...dataLayerEvents);
    });
    // seekStart
    player.on('seeking', () => {
      if (!sessionId.value) return;
      window.dataLayerQueue.push({
        event: 'seekStart',
        sessionId: sessionId.value,
        playhead: Math.floor(player.currentTime())
      });
    });
    // seekComplete
    player.on('seeked', async () => {
      if (!sessionId.value) return;
      window.dataLayerQueue.push({
        event: 'seekComplete',
        sessionId: sessionId.value,
        playhead: Math.floor(player.currentTime())
      });
    });
    // contentPause
    player.on('pause', async () => {
      if (!sessionId.value) return;
      window.dataLayerQueue.push({
        event: 'contentPause',
        sessionId: sessionId.value,
        playhead: Math.floor(player.currentTime())
      });
    });



    // contentComplete (ended)
    player.on('ended', async (e) => {
      if (!sessionId.value) return;
      const payload = {
        event: 'contentComplete',
        sessionId: sessionId.value,
        playhead: Math.floor(player.currentTime())
      };
      window.dataLayerQueue.push(payload);
    });
    // mediaEnd (dispose)
    player.on('dispose', async (e) => {
      if (!sessionId.value) return;
      const payload = {
        event: 'mediaEnd',
        sessionId: sessionId.value,
        playhead: Math.floor(player.currentTime())
      };
      window.dataLayerQueue.push(payload);
    });
  });
});

// Dispose of the player before unmounting the component
onBeforeUnmount(() => {
  if (player) {
    player.dispose();
  }
});

watch(
  () => props.options.sources,
  (newValues) => {
    if (player && newValues.length > 0) {
      player.src(newValues);
      nbError = 0; // Reset nb error when sources change
    }
  },
  { deep: true }
);
const setVolume = (e) => {
  e.preventDefault();

  if (e.code === 'ArrowUp') {
    player.volume(player.volume() + 0.1);
  } else {
    player.volume(player.volume() - 0.1);
  }
};
const toggleDV = () => {
  const audioTracks = player.audioTracks();

  if (!audioTracks || audioTracks.length === 0) {
    return false;
  }

  let mainTrack = null;
  let descriptionTrack = null;

  // Identifier les pistes disponibles et la piste actuellement active
  for (let i = 0; i < audioTracks.length; i++) {
    const track = audioTracks[i];

    if (track.kind === 'main') {
      mainTrack = track;
    } else if (track.kind === 'descriptions' || track.kind === 'main-desc') {
      descriptionTrack = track;
    }
  }

  // Si aucune piste de description n'est disponible
  if (!descriptionTrack) {
    return false;
  }

  // Basculer entre les pistes
  if (descriptionTrack.enabled) {
    // Actuellement sur la vidéodescription, basculer vers l'audio principal
    descriptionTrack.enabled = false;
    mainTrack.enabled = true;
  } else {
    // Actuellement sur l'audio principal, basculer vers la vidéodescription
    mainTrack.enabled = false;
    descriptionTrack.enabled = true;
  }
};

const toggleCaptions = () => {
  const tracks = player.textTracks();
  const track = tracks.tracks_.find((track) => track.language !== '');

  if (track) {
    if (track.mode == 'showing') {
      track.mode = 'disabled';

    } else {
      track.mode = 'showing';
    }
  }
};
</script>

<style lang="scss">
//SKIN custom (with progress bar at top)

// ===== VARIABLES =====
$primary-color: #d1121b;
$primary-hover-color: #a70e16;
$selected-bg-color: #a70e16;
$control-bar-color: #f9371d;
$menu-bg-color: rgba(43, 51, 63, 1);
$border-color: #757575;
$focus-outline-color: #02929a;

// ===== PLAYER SKIN CUSTOMIZATION =====

// Play Button
.vjs-big-play-button:focus-visible {
  outline: 2.5px solid $focus-outline-color;
  outline-offset: 2px;
}

// Control Bar
.vjs-control-bar {
  font-size: 12px;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
     rgba(0, 0, 0, 0.5) 45%,
    rgba(0, 0, 0, 0.9) 100%
  ) !important;
}

// ===== PROGRESS CONTROLS =====
.vjs-progress-control {
  position: absolute !important;
  bottom: 26px; /* The height of the ControlBar minus 4px. */
  left: 0;
  right: 0;
  width: 100% !important;
  height: 10px; /* the height must be reduced from 30 to 10px in order to allow the buttons below (e.g. play) to be pushed */
}

.vjs-progress-holder {
  /* needed to have a real 100% width display. */
  margin-left: 0px;
  margin-right: 0px;

  .vjs-play-progress,
  .vjs-load-progress,
  .vjs-load-progress div {
    border-radius: 6px !important;
  }
}

.video-js .vjs-play-progress {
  background-color: $control-bar-color;
  color: $control-bar-color;
}

// ===== TIME CONTROLS =====
.vjs-time-control {
  &.vjs-current-time,
  &.vjs-duration,
  &.vjs-time-divider {
    display: block;
  }

  &.vjs-current-time {
    padding-right: 6px;
  }

  &.vjs-duration {
    padding-left: 6px;
    margin-right: auto;
  }

  &.vjs-time-divider {
    padding-left: 0;
    padding-right: 0;
    min-width: auto;
  }

  &.vjs-remaining-time {
    display: none;
    margin-right: auto;
  }
}

// ===== SKIP CONTROLS =====
.vjs-skip-forward-10,
.vjs-skip-backward-10 {
  width: 38px !important;

  .vjs-icon-placeholder::before {
    font-size: 1rem;
    top: 4px;
  }
}

// ===== VOLUME CONTROLS =====
.video-js .vjs-volume-bar {
  color: $control-bar-color;
}

.video-js .vjs-volume-level {
  background-color: $control-bar-color;
}

// ===== PLAYBACK RATE CONTROL =====
.vjs-playback-rate .vjs-playback-rate-value {
  font-size: 14px;
  line-height: 2.5;
}

// ===== MENU CONTROLS =====
.vjs-menu .vjs-menu-content {
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  background-color: $menu-bg-color !important;
}

.vjs-menu li {
  border-bottom: 1px solid $border-color;
  margin: 0 0.25rem;
  font-size: 14px;

  &:first-child {
    margin-top: 0.25rem;
  }

  &:last-child {
    margin-bottom: 0.25rem;
    border-bottom: none;
  }
}

// Selected menu items
.vjs-menu li.vjs-selected,
.vjs-menu li.vjs-selected:focus,
.vjs-menu li.vjs-selected:hover,
.js-focus-visible .vjs-menu li.vjs-selected:hover {
  background-color: $selected-bg-color;
  color: white;
}

// ===== SUBTITLES & AUDIO CONTROLS =====
.vjs-subs-caps-button,
.vjs-audio-button {
  .vjs-menu .vjs-menu-content {
    width: calc(100% + 2rem);
    right: 0;
  }

  .vjs-menu li {
    padding: 0.5rem 0.25rem;
    line-height: 1.1;
    text-transform: none;

    span::first-letter {
      text-transform: uppercase;
    }
  }
}

// Subtitles control icons
.video-js .vjs-subs-caps-button {
  .vjs-icon-placeholder:before {
    content: "";
    width: 20px;
    display: inline-block;
    margin-left: 14px;
    background-color: white;
    -webkit-mask: url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14 2c.368 0 .667.317 .667.707v8.476c0 .39-.299.707-.667.707H6l.427 1.665a.365.365 0 01-.087.344.321.321 0 01-.471-.003L4 11.889H2c-.368 0-.667-.316-.667-.706V2.707C1.333 2.317 1.632 2 2 2zm-.667 1.413H2.667v7.063h10.666V3.413zm-2 4.233a.667.667 0 01.667.667v.079a.667.667 0 01-.667.667H4.667a.667.667 0 01-.667-.667v-.078a.667.667 0 01.667-.667h6.666zm0-2.823a.667.667 0 01.667.667v.079a.667.667 0 01-.667.667H4.667a.667.667 0 01-.667-.667v-.079a.667.667 0 01.667-.667h6.666z'%3E%3C/path%3E%3C/svg%3E") no-repeat center;
    mask: url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14 2c.368 0 .667.317 .667.707v8.476c0 .39-.299.707-.667.707H6l.427 1.665a.365.365 0 01-.087.344.321.321 0 01-.471-.003L4 11.889H2c-.368 0-.667-.316-.667-.706V2.707C1.333 2.317 1.632 2 2 2zm-.667 1.413H2.667v7.063h10.666V3.413zm-2 4.233a.667.667 0 01.667.667v.079a.667.667 0 01-.667.667H4.667a.667.667 0 01-.667-.667v-.078a.667.667 0 01.667-.667h6.666zm0-2.823a.667.667 0 01.667.667v.079a.667.667 0 01-.667.667H4.667a.667.667 0 01-.667-.667v-.079a.667.667 0 01.667-.667h6.666z'%3E%3C/path%3E%3C/svg%3E") no-repeat center;
    -webkit-mask-size: contain;
    mask-size: contain;
    font-family: inherit;
  }

  .vjs-menu .vjs-menu-item {
    .vjs-icon-placeholder{
        display:none !important;
    }
    &.vjs-selected{
      background-color: white;
      color: black;
    }
  }
  &.vjs-captions-active {
    .vjs-icon-placeholder:before {
      content: "";
      background-color: $control-bar-color;
    }
    .vjs-menu .vjs-menu-item {
      &.vjs-selected{
        background-color: $selected-bg-color;
        color: white;
      }
    }
  }
}

// Audio-VD control
.video-js .vjs-audio-button {
  display: flex;
  align-items: center;
  text-align: center;

  .vjs-icon-placeholder:before {
    content: "";
    width: 20px;
    display: inline-block;
    margin-left: 14px;
    background-color: white;
    -webkit-mask: url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.531 1.333C11.949 1.51 14.667 4.427 14.667 8S11.949 14.49 8.531 14.667c1.907-.717 3.41-2.269 4.105-4.239C13.934 6.747 12.096 2.675 8.531 1.333zM6.267 2.578c2.867.047 5.167 2.461 5.167 5.422 0 2.961-2.3 5.375-5.167 5.422 1.433-.593 2.569-1.767 3.144-3.247 1.163-2.993-.244-6.395-3.144-7.597zM4.969 4.663c1.785 0 3.233 1.493 3.233 3.337 0 1.844-1.448 3.337-3.233 3.337H1.333V4.663zM4.969 6.331H2.969v3.337h2.001c.893 0 1.617-.747 1.617-1.668s-.724-1.669-1.618-1.669z'%3E%3C/path%3E%3C/svg%3E") no-repeat center;
    mask: url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.531 1.333C11.949 1.51 14.667 4.427 14.667 8S11.949 14.49 8.531 14.667c1.907-.717 3.41-2.269 4.105-4.239C13.934 6.747 12.096 2.675 8.531 1.333zM6.267 2.578c2.867.047 5.167 2.461 5.167 5.422 0 2.961-2.3 5.375-5.167 5.422 1.433-.593 2.569-1.767 3.144-3.247 1.163-2.993-.244-6.395-3.144-7.597zM4.969 4.663c1.785 0 3.233 1.493 3.233 3.337 0 1.844-1.448 3.337-3.233 3.337H1.333V4.663zM4.969 6.331H2.969v3.337h2.001c.893 0 1.617-.747 1.617-1.668s-.724-1.669-1.618-1.669z'%3E%3C/path%3E%3C/svg%3E") no-repeat center;
    -webkit-mask-size: contain;
    mask-size: contain;
    font-family: inherit;
  }
  .vjs-menu .vjs-menu-item {
    .vjs-icon-placeholder{
        display:none !important;
    }
    &.vjs-selected{
      background-color: white;
      color: black;
    }
  }
  &.vjs-audio-dv-active {
    .vjs-icon-placeholder:before {
      content: "";
      background-color: $control-bar-color;
    }
    .vjs-menu .vjs-menu-item {
      &.vjs-selected{
        background-color: $selected-bg-color;
        color: white;
      }
    }
  }
}


// ===== MODAL & SETTINGS =====
.vjs-text-track-settings .vjs-modal-dialog-content {
  padding: 20px 24px 10px 24px;
  font-size: 14px;
}

.vjs-track-settings-controls button {
  background-image: none;
  background-color: $primary-color;
  font-weight: 600;
  color: white;
  padding: 0.4rem 1rem;
  border-radius: 4px;

  &:hover {
    color: white;
    background-color: $primary-hover-color;
  }
}

.vjs-control.vjs-close-button {
  font-size: 1rem;
}

.vjs-text-track-settings fieldset span {
  display: inline-block;
  padding: 0 1.5rem 0.8em 0;
}

.vjs-text-track-settings fieldset span > select,
.vjs-track-setting > select {
  background-color: #dfdfdf;
  border-radius: 6px;
  border-color: #dfdfdf;
  border-width: 4px;
}

.vjs-track-setting > select {
  margin-bottom: 1.2em;
}

// ===== RESPONSIVE BREAKPOINTS =====

// Tiny to Small screens
.vjs-layout-tiny,
.vjs-layout-x-small,
.vjs-layout-small {
  .vjs-picture-in-picture-control {
    display: none;
  }

  .vjs-menu-button-popup .vjs-menu .vjs-menu-content {
    max-height: 100px;
  }

  .vjs-playback-rate {
    display: none !important;
  }

  .vjs-time-control {
    &.vjs-current-time,
    &.vjs-duration,
    &.vjs-time-divider {
      display: none !important;
    }

    &.vjs-remaining-time {
      display: block !important;
    }
  }

  .vjs-progress-control {
    display: block !important;
    bottom: 4px;
  }
}

// Tiny to Medium screens
.vjs-layout-tiny,
.vjs-layout-x-small,
.vjs-layout-small,
.vjs-layout-medium {
  .vjs-control {
    width: 2.25rem;
  }

  .vjs-control-bar {
    font-size: 10px;
  }

  .vjs-subs-caps-button,
  .vjs-audio-button {
    .vjs-icon-placeholder:before {
      width: 16px;
    }
  }

  .vjs-skip-forward-10,
  .vjs-skip-backward-10 {
    width: 32px !important;

    .vjs-icon-placeholder::before {
      font-size: 14px;
      top: 3px;
    }
  }

  .vjs-playback-rate {
    display: block !important;

    .vjs-playback-rate-value {
      font-size: 10px;
      line-height: 3;
    }
  }

  .vjs-menu li {
    border-bottom: 1px solid $border-color;
    margin: 0 0.15rem;
    font-size: 12px;

    &:first-child {
      margin-top: 0.25rem;
    }

    &:last-child {
      margin-bottom: 0.25rem;
      border-bottom: none;
    }
  }

  .vjs-text-track-settings .vjs-modal-dialog-content {
    padding: 10px;
    font-size: 12px;
  }
}

// Small to Medium screens
.vjs-layout-small,
.vjs-layout-medium {
  .vjs-menu-button-popup .vjs-menu .vjs-menu-content {
    max-height: 200px;
  }
}
</style>
